import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import jsPDF from 'jspdf';
import { fmtPKTTime, utcToPKTInput } from '../../helpers/time';
import { notify } from '../../helpers/utils';
import { RefreshCw, Download, Search, X, ChevronDown, ChevronUp, ChevronRight, Plus, Loader2 } from 'lucide-react';

// ─── DESIGN TOKENS (iNOVAA dark) ──────────────────────────────────
const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textStrong: 'var(--nd-text-strong)',
  textMuted: 'var(--nd-tx35)',
  blue: '#3B82F6',
  blueLight: '#60A5FA',
  green: '#22D3A5',
  amber: '#F59E0B',
  red: '#F87171',
};

const API = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = API ? API.replace('/api', '') : '';

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-att { font-variant-numeric: tabular-nums; -webkit-font-smoothing: antialiased; }
  .nv-att * { box-sizing: border-box; }
  .nv-att ::-webkit-scrollbar { width: 7px; height: 7px; }
  .nv-att ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-att ::-webkit-scrollbar-track { background: transparent; }

  .nv-att .nv-card { background: var(--nd-surface); border: 1px solid var(--nd-w06); border-radius: 12px; overflow: hidden; }
  .nv-att .nv-crumb { font-size: 12.5px; color: var(--nd-tx35); font-weight: 500; }
  .nv-att .nv-section-title { font-size: 14px; color: var(--nd-text-strong); font-weight: 600; }
  .nv-att .nv-hint { font-size: 12.5px; color: var(--nd-tx35); line-height: 1.55; }
  .nv-att .nv-empty { text-align: center; color: var(--nd-tx35); padding: 48px 16px; font-size: 13px; }

  /* ── table ── */
  .nv-att table { border-collapse: separate; border-spacing: 0; width: 100%; }
  .nv-att th {
    font-size: 12px; color: var(--nd-tx35); font-weight: 600;
    text-align: left; padding: 11px 16px; white-space: nowrap;
    position: sticky; top: 0; z-index: 2;
    background: var(--nd-surface); border-bottom: 1px solid var(--nd-w08);
  }
  .nv-att th.nv-sortable { cursor: pointer; user-select: none; }
  .nv-att th.nv-sortable:hover { color: var(--nd-text); }
  .nv-att th.nv-active { color: var(--nd-text-strong); }
  .nv-att td { font-size: 13px; color: var(--nd-text); padding: 12px 16px; border-bottom: 1px solid var(--nd-w05); vertical-align: middle; }
  .nv-att tbody tr:last-child td { border-bottom: none; }
  .nv-att tbody tr:hover td { background: var(--nd-w04); }
  .nv-att tr.nv-drill { cursor: pointer; }
  .nv-att td.nv-num { white-space: nowrap; }
  .nv-att td .nv-sub { font-size: 12px; color: var(--nd-tx35); margin-top: 3px; }
  .nv-att tfoot td {
    position: sticky; bottom: 0; z-index: 2;
    background: var(--nd-surface); border-top: 1px solid var(--nd-w08); border-bottom: none;
    font-weight: 600; font-size: 13px;
  }
  .nv-att .nv-scroll { max-height: 640px; overflow: auto; }

  /* ── form controls ── */
  .nv-att input, .nv-att select {
    height: 36px; background: var(--nd-surface); border: 1px solid var(--nd-w10); border-radius: 8px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px;
    padding: 0 12px; outline: none; transition: border-color .15s, box-shadow .15s;
  }
  .nv-att td input { height: 32px; }
  .nv-att input[type="checkbox"] { height: auto; }
  .nv-att input:focus, .nv-att select:focus { border-color: #3B82F6; box-shadow: 0 0 0 3px rgba(59,130,246,0.18); }
  .nv-att input[type="date"], .nv-att input[type="month"] { color-scheme: dark; }
  :root[data-theme="light"] .nv-att input[type="date"], :root[data-theme="light"] .nv-att input[type="month"] { color-scheme: light; }

  .nv-att .nv-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 7px; height: 36px; padding: 0 14px;
    background: var(--nd-surface); border: 1px solid var(--nd-w10); border-radius: 8px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
    cursor: pointer; white-space: nowrap; transition: background .15s, border-color .15s;
  }
  .nv-att .nv-btn:hover:not(:disabled) { background: var(--nd-w05); border-color: var(--nd-w15); }
  .nv-att .nv-btn:disabled { color: var(--nd-w20); cursor: not-allowed; }
  .nv-att .nv-btn.nv-sm { height: 30px; padding: 0 11px; font-size: 12.5px; }
  .nv-att .nv-btn.nv-ghost { background: none; border-color: transparent; color: var(--nd-tx35); }
  .nv-att .nv-btn.nv-ghost:hover:not(:disabled) { color: var(--nd-text); background: var(--nd-w05); }
  .nv-att .nv-btn.nv-dashed { background: none; border-style: dashed; color: var(--nd-tx35); }
  .nv-att .nv-btn.nv-primary { background: #3B82F6; border-color: #3B82F6; color: #fff; }
  .nv-att .nv-btn.nv-primary:hover:not(:disabled) { background: #2563EB; border-color: #2563EB; }
  .nv-att .nv-btn.nv-primary:disabled { background: var(--nd-w05); border-color: var(--nd-w10); color: var(--nd-w20); }

  .nv-att .nv-seg { display: inline-flex; gap: 2px; padding: 3px; height: 36px; background: var(--nd-w04); border: 1px solid var(--nd-w08); border-radius: 8px; }
  .nv-att .nv-seg button {
    padding: 0 12px; background: none; border: none; border-radius: 6px; color: var(--nd-tx35);
    font-family: 'DM Sans', sans-serif; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap;
  }
  .nv-att .nv-seg button:hover { color: var(--nd-text); }
  .nv-att .nv-seg button.nv-on { background: var(--nd-w10); color: var(--nd-text-strong); box-shadow: 0 1px 2px rgba(0,0,0,0.25); }

  .nv-att .nv-chip {
    display: inline-flex; align-items: center; gap: 6px; height: 26px; padding: 0 9px 0 10px; border-radius: 6px;
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer;
    border: 1px solid var(--nd-w10); background: var(--nd-w05); color: var(--nd-text);
  }
  .nv-att .nv-chip:hover { border-color: var(--nd-w20); }
  .nv-att .nv-badge {
    display: inline-flex; align-items: center; font-size: 11px; font-weight: 600;
    padding: 2px 8px; border-radius: 999px; white-space: nowrap; line-height: 1.5;
  }
  .nv-att .nv-badge.nv-sup { background: rgba(59,130,246,0.14); color: #93C5FD; }
  .nv-att .nv-fieldlabel { font-size: 12px; color: var(--nd-tx35); font-weight: 600; margin-bottom: 7px; }
  .nv-att .nv-spin { animation: nv-spin 0.9s linear infinite; }
  @keyframes nv-spin { to { transform: rotate(360deg); } }
  .nv-att .nv-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

  /* ── layout ── */
  .nv-att .nv-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
  .nv-att .nv-filterbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
  .nv-att .nv-filterbar select { min-width: 150px; }

  .nv-att .nv-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); }
  .nv-att .nv-stat {
    padding: 18px 20px; border: none; border-right: 1px solid var(--nd-w06); background: none;
    text-align: left; font-family: 'DM Sans', sans-serif; min-width: 0; color: inherit;
  }
  .nv-att .nv-stat:last-child { border-right: none; }
  .nv-att .nv-stat-label { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--nd-tx35); font-weight: 500; }
  .nv-att .nv-stat-value { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; line-height: 1; margin-top: 10px; white-space: nowrap; color: var(--nd-text-strong); }
  .nv-att .nv-stat-sub { font-size: 12px; color: var(--nd-tx35); margin-top: 7px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .nv-att button.nv-stat { cursor: pointer; transition: background .15s; }
  .nv-att button.nv-stat:hover:not(:disabled) { background: var(--nd-w04); }
  .nv-att button.nv-stat:disabled { cursor: default; }

  .nv-att .nv-tabbar { display: flex; align-items: center; gap: 12px; padding: 0 16px 0 8px; border-bottom: 1px solid var(--nd-w06); flex-wrap: wrap; }
  .nv-att .nv-tabs { display: flex; overflow-x: auto; scrollbar-width: none; min-width: 0; }
  .nv-att .nv-tabs::-webkit-scrollbar { display: none; }
  .nv-att .nv-tab {
    padding: 14px 12px; background: none; border: none; border-bottom: 2px solid transparent; margin-bottom: -1px;
    color: var(--nd-tx35); font-family: 'DM Sans', sans-serif; font-weight: 600; font-size: 13px;
    cursor: pointer; transition: color .15s, border-color .15s; white-space: nowrap;
  }
  .nv-att .nv-tab:hover { color: var(--nd-text); }
  .nv-att .nv-tab.nv-on { color: var(--nd-text-strong); border-bottom-color: #3B82F6; }
  .nv-att .nv-subbar { padding: 10px 20px; border-bottom: 1px solid var(--nd-w06); background: var(--nd-w03, rgba(255,255,255,0.02)); }
  .nv-att .nv-count { font-size: 12.5px; color: var(--nd-tx35); white-space: nowrap; }
  .nv-att .nv-foot { padding: 12px 20px; border-top: 1px solid var(--nd-w06); font-size: 12px; color: var(--nd-tx35); line-height: 1.6; }

  @media (max-width: 1100px) {
    .nv-att .nv-stat { border-bottom: 1px solid var(--nd-w06); }
  }
  @media (max-width: 680px) {
    .nv-att { padding: 16px !important; }
    .nv-att .nv-head { flex-direction: column; align-items: flex-start; }
    .nv-att .nv-filterbar > * { flex: 1 1 100%; }
    .nv-att .nv-filterbar input[type="date"] { flex: 1 1 0; min-width: 0; }
    .nv-att .nv-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .nv-att .nv-stat { padding: 14px 16px; }
    .nv-att .nv-stat-value { font-size: 20px; }
    .nv-att .nv-seg { overflow-x: auto; }
  }
`;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const UNASSIGNED = 'Unassigned';

const VIEWS = [
  { key: 'person',   tab: 'Person',        col: 'Field Officer' },
  { key: 'client',   tab: 'Client',        col: 'Client' },
  { key: 'city',     tab: 'City',          col: 'City' },
  { key: 'coverage', tab: "Who's missing", col: 'Client' },
  { key: 'checkins', tab: 'Check-in log',  col: 'Name' },
  { key: 'teams',    tab: 'Teams',         col: 'Team' },
];

// Check-in log CSV keeps every field as its own column (the on-screen table folds some together).
const LOG_CSV_COLS = [
  { key: 'fo_name', label: 'Name', type: 'text' },
  { key: 'attendance_date', label: 'Date', type: 'text' },
  { key: '_seqOf', label: 'Of day', type: 'num' },
  { key: 'clock_in_at', label: 'In', type: 'text' },
  { key: 'clock_out_at', label: 'Out', type: 'text' },
  { key: 'hours_worked', label: 'Hours', type: 'num', hrs: true },
  { key: 'client_name', label: 'Client', type: 'text' },
  { key: 'site_name', label: 'Site', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'shift', label: 'Shift', type: 'text' },
  { key: 'status', label: 'Status', type: 'text' },
];

const STATES = {
  present:   { label: 'clocked in',  color: '#22D3A5', bg: 'rgba(34,211,165,0.12)',  border: 'rgba(34,211,165,0.30)' },
  elsewhere: { label: 'another site', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.30)' },
  absent:    { label: 'no clock-in', color: '#F87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.32)' },
};

// ─── HELPER TO IDENTIFY SUPERVISORS ──────────────────────────────
const checkIsSupervisor = (r) => {
  if (r.is_supervisor) return true;
  const role = r.role || r.designation || '';
  return role.toLowerCase().includes('supervisor');
};

// ─── PAYROLL CONSTANTS ────────────────────────────────────────────
const DEFAULT_SALARY = 40000;
const SALARY_STORAGE_KEY = 'novaa_payroll_salaries';
const MISSING_OUT_PENALTY_UNIT = 5;
// Shift start in PKT minutes after midnight, keyed by site_schedules.shift. Job History's SHIFT_CONFIG and the
// calendar's SHIFTS disagree on these; payroll uses the times ops confirmed (morning = 8 AM). A punch more than
// LATE_GRACE_MIN after its job's shift start is late; only admin-approved lates count toward the deduction.
const SHIFT_START_MIN = { morning: 8 * 60, afternoon: 11 * 60, evening: 15 * 60, night: 19 * 60, overnight: 22 * 60 };
const LATE_GRACE_MIN = 15;
const LATES_PER_DEDUCTION_DAY = 5;
// FO commission, per site per day: (system_size kW − 100) split across everyone who clocked in there that day
// (supervisors included), × Rs. 2 per kW. Only for clients with Commission ON in Mandate Config, and only
// sites over 100 kW.
const COMMISSION_BASE_KW = 100;
const COMMISSION_RS_PER_KW = 2;

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const windowStart = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthStartStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; };
const currentPayrollMonth = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('-');
  if (!y || !m || !d) return s;
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
};
const fmtRange = (a, b) => {
  if (!a || !b) return null;
  const [ay, am, ad] = String(a).split('-');
  const [by, bm, bd] = String(b).split('-');
  if (!ay || !by) return null;
  const left = `${MONTHS[Number(am) - 1]} ${Number(ad)}`;
  const right = `${MONTHS[Number(bm) - 1]} ${Number(bd)}, ${by}`;
  return ay === by ? `${left} – ${right}` : `${left}, ${ay} – ${right}`;
};
const monthLabel = (ym) => {
  const [y, m] = String(ym).split('-').map(Number);
  if (!y || !m) return ym;
  return `${MONTHS[m - 1]} ${y}`;
};
const fmtTime = (ts) => fmtPKTTime(ts, '—');
// API sends decimal hours (4.77 = 4h 46m) — round to whole minutes first so 59.6m carries into the hour.
const fmtHours = (h) => {
  if (h === null || h === undefined || h === '') return '—';
  const n = Number(h);
  if (!Number.isFinite(n)) return '—';
  const mins = Math.round(n * 60);
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};
const pct = (n, total) => (!total ? 0 : Math.round((n / total) * 100));
const fmtRs = (n) => `Rs. ${Math.round(Number(n) || 0).toLocaleString()}`;

// attendance_date is a plain DATE — read it as a local calendar day, never through a timezone.
const isSunday = (ymd) => {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
};
// Minutes late past the grace window for one punch, or null if on time / shift unknown.
const lateMinutesOf = (rec) => {
  const start = SHIFT_START_MIN[String(rec.shift || '').toLowerCase().trim()];
  const local = utcToPKTInput(rec.clock_in_at); // "YYYY-MM-DDTHH:mm" in Pakistan time
  if (start == null || !local) return null;
  const [hh, mm] = local.slice(11, 16).split(':').map(Number);
  let diff = hh * 60 + mm - start;
  if (diff > 720) diff -= 1440; // overnight shifts cross midnight
  if (diff < -720) diff += 1440;
  return diff > LATE_GRACE_MIN ? diff : null;
};
const fmtLate = (mins) => (mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m late` : `${mins}m late`);

const csvCell = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return `"${String(v).replace(/"/g, '""')}"`;
};
const saveCSV = (filename, headers, rows) => {
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
const sortBy = (arr, key, dir, type) => {
  const sign = dir === 'asc' ? 1 : -1;
  return [...arr].sort((x, y) => {
    const a = x[key];
    const b = y[key];
    const aEmpty = a === null || a === undefined || a === '';
    const bEmpty = b === null || b === undefined || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (type === 'num') return (Number(a) - Number(b)) * sign;
    return String(a).localeCompare(String(b)) * sign;
  });
};

const loadSalaries = () => {
  try {
    const raw = localStorage.getItem(SALARY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) { return {}; }
};

const PDF_C = {
  navy: [12, 19, 34],
  blue: [59, 130, 246],
  blueLight: [96, 165, 250],
  green: [16, 150, 116],
  greenBg: [220, 252, 231],
  red: [220, 38, 38],
  amber: [180, 95, 6],
  text: [30, 41, 59],
  muted: [110, 125, 150],
  border: [226, 232, 240],
  rowAlt: [247, 249, 252],
  white: [255, 255, 255],
};

const buildPayslipPDF = (row, ym) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const mX = 16;
  let y;

  doc.setFillColor(...PDF_C.navy);
  doc.rect(0, 0, pageW, 40, 'F');
  doc.setFillColor(...PDF_C.blue);
  doc.rect(0, 40, pageW * 0.55, 1.6, 'F');
  doc.setFillColor(...PDF_C.green);
  doc.rect(pageW * 0.55, 40, pageW * 0.45, 1.6, 'F');

  doc.setTextColor(...PDF_C.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(21);
  doc.text('iNOVAA', mX, 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(170, 186, 214);
  doc.text('C&I MANAGEMENT  ·  PAYSLIP', mX, 25.5);

  doc.setFontSize(8.5);
  doc.setTextColor(140, 158, 190);
  const genDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  doc.text(`Generated ${genDate}`, pageW - mX, 18, { align: 'right' });
  doc.text(monthLabel(ym), pageW - mX, 25.5, { align: 'right' });

  y = 53;
  doc.setTextColor(...PDF_C.text);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text(row.name, mX, y);
  doc.setDrawColor(...PDF_C.border);
  doc.setLineWidth(0.3);
  doc.line(mX, y + 4, pageW - mX, y + 4);

  y += 12;
  const gap = 6;
  const cardW = (pageW - mX * 2 - gap * 2) / 3;
  const cardH = 22;
  const cards = [
    { label: 'PAID DAYS', value: `${row.paidDays} / ${row.daysInMonth}`, color: PDF_C.blue },
    { label: 'DEDUCTIONS + STL', value: `Rs. ${(row.deductionAmount + row.stl).toFixed(0)}`, color: (row.deductionAmount + row.stl) > 0 ? PDF_C.red : PDF_C.muted },
    { label: 'NET PAYABLE', value: `Rs. ${row.net.toFixed(0)}`, color: PDF_C.green },
  ];
  cards.forEach((c, i) => {
    const x = mX + i * (cardW + gap);
    doc.setFillColor(...PDF_C.rowAlt);
    doc.setDrawColor(...PDF_C.border);
    doc.setLineWidth(0.25);
    doc.roundedRect(x, y, cardW, cardH, 2.2, 2.2, 'FD');
    doc.setFillColor(...c.color);
    doc.roundedRect(x, y, 1.4, cardH, 0.7, 0.7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14.5);
    doc.setTextColor(...c.color);
    doc.text(c.value, x + 6, y + 10.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...PDF_C.muted);
    doc.text(c.label, x + 6, y + 17);
  });

  y += cardH + 12;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF_C.muted);
  doc.text('BREAKDOWN', mX, y);
  y += 5;

  const tableW = pageW - mX * 2;
  const rowH = 8;
  const lines = [
    ['Base Salary', `Rs. ${row.base.toLocaleString()}`],
    ['Days in Month', String(row.daysInMonth)],
    ['Per-Day Rate', `Rs. ${row.perDay.toFixed(2)}`],
    ['Days Present', String(row.daysPresent)],
    ['Sundays Off (paid)', String(row.sundaysOff)],
    ['Gross (Per-Day × Paid Days)', `Rs. ${row.gross.toFixed(2)}`],
    [`Sundays Worked (${row.sundaysWorked} × 1 extra day)`, `+ Rs. ${row.sundayBonus.toFixed(2)}`],
    [`Commission (${row.commissions.length} site-day${row.commissions.length === 1 ? '' : 's'})`, `+ Rs. ${row.commission.toFixed(2)}`],
    [`Late: ${row.lates.length}, approved ${row.approvedLates} (1 day per ${LATES_PER_DEDUCTION_DAY})`, `${row.lateDeductionDays} day${row.lateDeductionDays === 1 ? '' : 's'}`],
    [`Missing Clock-outs: ${row.missingOut} (1 day per ${MISSING_OUT_PENALTY_UNIT})`, `${row.missingOutDeductionDays} day${row.missingOutDeductionDays === 1 ? '' : 's'}`],
    ['Deducted Amount', `- Rs. ${row.deductionAmount.toFixed(2)}`],
    ['Short Term Loan (STL)', `- Rs. ${row.stl.toFixed(2)}`],
  ];

  doc.setDrawColor(...PDF_C.border);
  doc.setLineWidth(0.2);
  lines.forEach(([label, value], i) => {
    if (i % 2 === 0) {
      doc.setFillColor(...PDF_C.rowAlt);
      doc.rect(mX, y, tableW, rowH, 'F');
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...PDF_C.text);
    doc.text(label, mX + 4, y + 5.5);
    doc.setFont('helvetica', 'bold');
    doc.text(value, pageW - mX - 4, y + 5.5, { align: 'right' });
    doc.line(mX, y + rowH, pageW - mX, y + rowH);
    y += rowH;
  });

  const netH = rowH + 3;
  doc.setFillColor(...PDF_C.greenBg);
  doc.rect(mX, y, tableW, netH, 'F');
  doc.setDrawColor(...PDF_C.green);
  doc.setLineWidth(0.5);
  doc.rect(mX, y, tableW, netH, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.setTextColor(...PDF_C.green);
  doc.text('NET PAYABLE', mX + 4, y + netH / 2 + 1.5);
  doc.text(`Rs. ${row.net.toFixed(2)}`, pageW - mX - 4, y + netH / 2 + 1.5, { align: 'right' });
  y += netH + 12;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...PDF_C.muted);
  const note = `To revise ${row.name}'s salary, Contact Finance Department.`;
  doc.text(doc.splitTextToSize(note, tableW), mX, y);

  return doc;
};

const AttendanceDashboard = () => {
  const [view, setView] = useState('person');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [activePreset, setActivePreset] = useState('all');
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [city, setCity] = useState('');
  const [attention, setAttention] = useState(''); 
  const [sort, setSort] = useState({ key: 'checkins', dir: 'desc' });
  const [showTrend, setShowTrend] = useState(true);

  const [rows, setRows] = useState([]);
  const [records, setRecords] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [coverageRange, setCoverageRange] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [onlyScheduled, setOnlyScheduled] = useState(false);
  const [teamRosterList, setTeamRosterList] = useState([]);
  const [teamRosterUsers, setTeamRosterUsers] = useState([]);
  const [teamRosterLoading, setTeamRosterLoading] = useState(false);
  const [teamDayAttendance, setTeamDayAttendance] = useState([]);
  const [teamsSelectedDate, setTeamsSelectedDate] = useState(todayStr);
  const [addMemberFor, setAddMemberFor] = useState(null);
  const [newMemberId, setNewMemberId] = useState('');
  const [addMemberSaving, setAddMemberSaving] = useState(false);
  const [kpis, setKpis] = useState({});
  const [daily, setDaily] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ clients: [], cities: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { full, thumb } — the original is only fetched once opened
  const [lightboxLoaded, setLightboxLoaded] = useState(false);

  const [payrollMonth, setPayrollMonth] = useState(currentPayrollMonth);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollError, setPayrollError] = useState('');
  const [lateApprovals, setLateApprovals] = useState(() => new Set()); // approved attendance ids
  const [stlByName, setStlByName] = useState({});                       // fo_name -> { amount, note }
  const [payrollOpen, setPayrollOpen] = useState(null);                 // expanded person name
  const [payrollBusy, setPayrollBusy] = useState('');                   // id/key of the save in flight
  const [salaries, setSalaries] = useState({}); // fo_name -> base salary, from payroll_salaries

  const isLog = view === 'checkins';
  const isCoverage = view === 'coverage';
  const isPayroll = view === 'payroll';
  const isTeams = view === 'teams';
  const activeView = VIEWS.find(v => v.key === view) || VIEWS[0];

  const columns = useMemo(() => {
    if (isCoverage) {
      return [
        { key: 'client_name', label: 'Client', type: 'text' },
        { key: 'city', label: 'City', type: 'text' },
        { key: 'scheduled_jobs', label: 'Jobs booked', type: 'num', hint: 'Jobs scheduled for this client in the selected range. 0 means the client had no work booked — its roster is listed for completeness, so those absences are not no-shows.' },
        { key: 'expected', label: 'Expected', type: 'num', hint: 'Everyone who has ever clocked in for this client. Inferred from attendance history — there is no assignment table in the system.' },
        { key: 'present', label: 'Clocked in', type: 'num', hint: 'Clocked in FOR THIS CLIENT during the range.' },
        { key: 'elsewhere', label: 'Elsewhere', type: 'num', hint: 'Clocked in during the range, but for a different client. At work — just not here.' },
        { key: 'absent', label: 'No clock-in', type: 'num', warn: true, hint: 'Did not clock in anywhere in the range. This is the real no-show figure.' },
        { key: 'coverage_pct', label: 'Coverage', type: 'num', hint: 'Clocked in as a share of expected.' },
      ];
    }
    if (isLog) {
      return [
        // On screen, shift / site / city sit under their parent cell to keep the table narrow
        // enough that Photos and Status stay visible; LOG_CSV_COLS keeps them as real columns.
        { key: 'fo_name', label: 'Field officer', type: 'text' },
        { key: '_photos', label: 'Photos', type: 'text', sortable: false },
        { key: 'attendance_date', label: 'Date', type: 'text' },
        { key: 'clock_in_at', label: 'Clock in', type: 'text' },
        { key: 'clock_out_at', label: 'Clock out', type: 'text' },
        { key: 'hours_worked', label: 'Hours', type: 'num', hrs: true },
        { key: 'client_name', label: 'Client & site', type: 'text' },
        { key: 'status', label: 'Status', type: 'text' },
      ];
    }
    if (isTeams) {
      return []; // Handled separately via custom grid layout
    }
    return [
      { key: 'label', label: activeView.col, type: 'text' },
      { key: 'jobs', label: 'Jobs', type: 'num', bar: T.blueLight, hint: 'Distinct jobs checked into. One job worked by two people counts once for each of them.' },
      { key: 'checkins', label: 'Check-ins', type: 'num', bar: T.blue, hint: 'Every shift counts: one per person, per day, per job. A double-shift day contributes two.' },
      view === 'person'
        ? { key: 'days', label: 'Days', type: 'num', hint: 'Distinct dates worked. Lower than check-ins whenever double shifts were worked.' }
        : { key: 'people', label: 'People', type: 'num', hint: 'Distinct field officers who checked in.' },
      { key: 'total_hours', label: 'Total hrs', type: 'num', hrs: true, hint: 'Sums only shifts that have a clock-out. Open shifts are excluded, so treat this as a lower bound.' },
      { key: 'avg_hours', label: 'Avg hrs/shift', type: 'num', hrs: true, hint: 'Average per shift, NOT per day. Someone working 3 shifts in a day totals roughly 3x this — use Total hrs for the real figure.' },
      { key: 'missing_clockout', label: 'No clock-out', type: 'num', warn: true, hint: 'Check-ins with no clock-out recorded — these are excluded from hours.' },
    ];
  }, [isLog, isCoverage, isTeams, view, activeView.col]);

  const buildQS = (extra = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.append('from', from);
    if (to) qs.append('to', to);
    if (clientId) qs.append('client_id', clientId);
    if (city) qs.append('city', city);
    Object.entries(extra).forEach(([k, v]) => v && qs.append(k, v));
    return qs.toString() ? `?${qs.toString()}` : '';
  };

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);

      if (isCoverage) {
        const res = await axios.get(`${API}/attendance/coverage${buildQS()}`);
        const list = (res.data?.rows || []).map(r => ({
          ...r,
          coverage_pct: pct(r.present, r.expected),
        }));
        setCoverage(list);
        setCoverageRange(res.data?.range || null);
        setKpis(res.data?.kpis || {});
        setRows([]); setRecords([]); setDaily([]);
        return;
      }

      const summaryGroup = isLog || isTeams ? 'person' : view;
      const calls = [axios.get(`${API}/attendance/summary${buildQS({ group_by: summaryGroup })}`)];
      if (isLog || isTeams) calls.push(axios.get(`${API}/attendance/records${buildQS()}`));
      const [sumRes, recRes] = await Promise.all(calls);
      setKpis(sumRes.data?.kpis || {});
      setDaily(sumRes.data?.daily || []);
      setRows(isLog || isTeams ? [] : (sumRes.data?.rows || []));
      setRecords(isLog || isTeams ? (recRes?.data?.records || []) : []);
      setCoverage([]); setCoverageRange(null);
    } catch (err) {
      setError('Failed to load attendance data');
      setRows([]); setRecords([]); setCoverage([]); setKpis({}); setDaily([]);
    } finally { setLoading(false); }
  };

  const fetchFilterOptions = async () => {
    const qs = new URLSearchParams();
    if (from) qs.append('from', from);
    if (to) qs.append('to', to);
    const q = qs.toString() ? `?${qs.toString()}` : '';
    try {
      const res = await axios.get(`${API}/attendance/filters${q}`);
      setFilterOptions({ clients: res.data?.clients || [], cities: res.data?.cities || [] });
    } catch (e) { }
  };

  const fetchPayroll = async () => {
    try {
      setPayrollLoading(true); setPayrollError('');
      // /payroll/month returns the WHOLE month (/attendance/records stops at 2000 rows) plus site kW,
      // approved lates and STL — see sowash-backend/routes/payrollRoutes.js.
      const res = await axios.get(`${API}/payroll/month?month=${payrollMonth}`);
      setPayrollRecords(res.data?.records || []);
      setLateApprovals(new Set((res.data?.approvals || []).map(a => a.attendance_id)));
      setStlByName(Object.fromEntries((res.data?.stl || []).map(s => [s.fo_name, { amount: Number(s.amount) || 0, note: s.note || '' }])));
      setSalaries(Object.fromEntries((res.data?.salaries || []).map(s => [s.fo_name, Number(s.base_salary) || 0])));
    } catch (e) {
      setPayrollRecords([]); setLateApprovals(new Set()); setStlByName({}); setSalaries({});
      setPayrollError(e.response?.status === 404
        ? 'Payroll API not found — deploy sowash-backend/routes/payrollRoutes.js and run the 2026-10-27 migration.'
        : (e.response?.data?.message || 'Failed to load payroll'));
    } finally { setPayrollLoading(false); }
  };

  const toggleLateApproval = async (attendanceId, approve) => {
    setPayrollBusy(`late-${attendanceId}`);
    try {
      if (approve) await axios.post(`${API}/payroll/late-approvals`, { attendance_id: attendanceId });
      else await axios.delete(`${API}/payroll/late-approvals/${attendanceId}`);
      setLateApprovals(prev => {
        const next = new Set(prev);
        approve ? next.add(attendanceId) : next.delete(attendanceId);
        return next;
      });
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not save the approval');
    } finally { setPayrollBusy(''); }
  };

  const saveStl = async (name, amount) => {
    const value = Math.max(0, Number(amount) || 0);
    if ((stlByName[name]?.amount || 0) === value) return;
    setPayrollBusy(`stl-${name}`);
    try {
      await axios.put(`${API}/payroll/stl`, { fo_name: name, month: payrollMonth, amount: value });
      setStlByName(prev => {
        const next = { ...prev };
        if (value > 0) next[name] = { ...(prev[name] || {}), amount: value };
        else delete next[name];
        return next;
      });
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not save the loan amount');
    } finally { setPayrollBusy(''); }
  };

  // ─── TEAM ROSTER (from Team Management: supervisor = team_lead_user_id) ──
  const fetchTeamRoster = async () => {
    try {
      setTeamRosterLoading(true);
      const [teamsRes, usersRes] = await Promise.all([
        axios.get(`${API}/schedule/teams?active_only=true`),
        axios.get(`${API}/schedule/users/available`),
      ]);
      setTeamRosterList(teamsRes.data?.teams || []);
      setTeamRosterUsers(usersRes.data?.users || []);
    } catch (e) {
      setTeamRosterList([]); setTeamRosterUsers([]);
    } finally { setTeamRosterLoading(false); }
  };

  const fetchTeamDayAttendance = async (date) => {
    try {
      const res = await axios.get(`${API}/attendance/records?from=${date}&to=${date}`);
      setTeamDayAttendance(res.data?.records || []);
    } catch (e) { setTeamDayAttendance([]); }
  };

  useEffect(() => { if (!isPayroll) fetchData(); }, [from, to, clientId, city, view]);
  useEffect(() => { fetchFilterOptions(); }, [from, to]);
  useEffect(() => { if (isPayroll) fetchPayroll(); }, [isPayroll, payrollMonth]);
  useEffect(() => { if (isTeams) fetchTeamRoster(); }, [isTeams]);
  useEffect(() => { if (isTeams) fetchTeamDayAttendance(teamsSelectedDate); }, [isTeams, teamsSelectedDate]);

  useEffect(() => {
    if (!isPayroll && !isTeams && !columns.some(c => c.key === sort.key)) {
      setSort(isLog ? { key: 'attendance_date', dir: 'desc' } : isCoverage ? { key: 'absent', dir: 'desc' } : { key: 'checkins', dir: 'desc' });
    }
  }, [columns, sort.key, isLog, isCoverage, isPayroll, isTeams]);

  const applyPreset = (key) => {
    setActivePreset(key);
    if (key === 'all') { setFrom(''); setTo(''); }
    else if (key === '7') { setFrom(windowStart(7)); setTo(todayStr()); }
    else if (key === '30') { setFrom(windowStart(30)); setTo(todayStr()); }
    else if (key === 'month') { setFrom(monthStartStr()); setTo(todayStr()); }
  };

  const clearAll = () => {
    setClientId(''); setClientLabel(''); setCity(''); setSearch(''); setAttention('');
    applyPreset('all');
  };

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort(s => (s.key === col.key
      ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key: col.key, dir: col.type === 'num' ? 'desc' : 'asc' }));
  };

  const switchView = (key) => { setView(key); setAttention(''); setExpanded(new Set()); };

  const drillInto = (row) => {
    if (view === 'client') {
      if (!row.group_id) return;
      setClientId(String(row.group_id)); setClientLabel(row.label); setView('person');
    } else if (view === 'city') {
      if (!row.label || row.label === UNASSIGNED) return;
      setCity(row.label); setView('person');
    }
  };
  const canDrill = (row) => (
    (view === 'client' && !!row.group_id) ||
    (view === 'city' && row.label && row.label !== UNASSIGNED)
  );

  const showAttention = (kind) => { setView('checkins'); setAttention(kind); };

  const teamUserName = (uid) => teamRosterUsers.find(u => u.id === uid)?.name || `User #${uid}`;

  const memberDayStatus = (name) => {
    const rec = teamDayAttendance.find(r => (r.fo_name || '').trim().toLowerCase() === (name || '').trim().toLowerCase());
    if (!rec) return { ...STATES.absent, label: 'ABSENT' };
    const raw = (rec.status || '').toLowerCase();
    if (raw.includes('late')) return { ...STATES.elsewhere, label: rec.status.toUpperCase() };
    if (raw.includes('absent')) return { ...STATES.absent, label: rec.status.toUpperCase() };
    return { ...STATES.present, label: (rec.status || 'present').toUpperCase() };
  };

  const toggleTeamExpand = (teamId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(teamId) ? next.delete(teamId) : next.add(teamId);
      return next;
    });
  };

  const availableUsersForTeam = (team) => {
    const memberIds = new Set(team.team_member_user_ids || []);
    return teamRosterUsers.filter(u => u.id !== team.team_lead_user_id && !memberIds.has(u.id));
  };

  const handleAddMember = async (team) => {
    if (!newMemberId) return;
    try {
      setAddMemberSaving(true);
      await axios.post(`${API}/schedule/teams/${team.id}/members`, { user_id: Number(newMemberId) });
      notify('success', 'Team member added');
      setAddMemberFor(null); setNewMemberId('');
      fetchTeamRoster();
    } catch (e) {
      notify('error', e.response?.data?.message || 'Failed to add team member');
    } finally { setAddMemberSaving(false); }
  };

  const getSalary = (name) => (salaries[name] != null ? salaries[name] : DEFAULT_SALARY);
  // Salaries live in the database (payroll_salaries) — saved on blur, shared by every admin.
  const saveSalary = async (name, value) => {
    const amount = Math.max(0, Number(value) || 0);
    if (amount === getSalary(name)) return;
    setPayrollBusy(`salary-${name}`);
    try {
      await axios.put(`${API}/payroll/salary`, { fo_name: name, base_salary: amount });
      setSalaries(prev => ({ ...prev, [name]: amount }));
      notify('success', `Salary saved for ${name}`);
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not save the salary');
    } finally { setPayrollBusy(''); }
  };

  // Salaries typed before the database existed are still in this browser's localStorage. Offer a one-time
  // copy of the ones the database does not have yet (the server never overwrites an existing salary).
  const browserOnlySalaries = useMemo(() => {
    if (payrollLoading || payrollError) return {};
    const local = loadSalaries();
    return Object.fromEntries(Object.entries(local).filter(([name]) => salaries[name] == null));
  }, [salaries, payrollLoading, payrollError]);
  const importBrowserSalaries = async () => {
    setPayrollBusy('salary-import');
    try {
      const res = await axios.post(`${API}/payroll/salary/import`, { salaries: browserOnlySalaries });
      try { localStorage.removeItem(SALARY_STORAGE_KEY); } catch (e) {}
      notify('success', `${res.data?.imported ?? 0} salaries saved to the database`);
      await fetchPayroll();
    } catch (e) {
      notify('error', e.response?.data?.message || 'Could not import salaries');
    } finally { setPayrollBusy(''); }
  };

  const payrollRows = useMemo(() => {
    const [y, m] = payrollMonth.split('-').map(Number);
    const daysInMonth = (y && m) ? new Date(y, m, 0).getDate() : 30;
    // Sundays are paid days off. In the current month only Sundays already reached count, so a
    // mid-month view does not pay out Sundays that have not happened yet.
    const today = todayStr();
    const monthSundays = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const ymd = `${y}-${pad(m)}-${pad(d)}`;
      if (ymd <= today && isSunday(ymd)) monthSundays.push(ymd);
    }

    // Commission is earned per site per day, split across everyone who clocked in there that day.
    const siteDays = new Map();
    payrollRecords.forEach(r => {
      const kw = Number(r.system_size);
      // Only clients whose Commission switch is ON (Mandate Config → ci_client_mandates.commission_enabled).
      if (!r.commission_enabled || !r.site_id || !r.attendance_date || !r.fo_name || !(kw > COMMISSION_BASE_KW)) return;
      const key = `${r.site_id}|${r.attendance_date}`;
      if (!siteDays.has(key)) siteDays.set(key, { site: r.site_name || `Site #${r.site_id}`, client: r.client_name, date: r.attendance_date, kw, people: new Set() });
      siteDays.get(key).people.add(r.fo_name);
    });
    const commissionByName = new Map();
    siteDays.forEach(sd => {
      const perHead = ((sd.kw - COMMISSION_BASE_KW) / sd.people.size) * COMMISSION_RS_PER_KW;
      sd.people.forEach(name => {
        if (!commissionByName.has(name)) commissionByName.set(name, []);
        commissionByName.get(name).push({ ...sd, people: sd.people.size, amount: perHead });
      });
    });

    const byPerson = new Map();
    payrollRecords.forEach(r => {
      const name = r.fo_name || UNASSIGNED;
      if (!byPerson.has(name)) byPerson.set(name, { days: new Set(), missingOut: 0, checkins: 0, lates: [] });
      const p = byPerson.get(name);
      if (r.attendance_date) p.days.add(r.attendance_date);
      p.checkins += 1;
      if (!r.clock_out_at) p.missingOut += 1;
      const lateBy = lateMinutesOf(r);
      if (lateBy != null) {
        p.lates.push({ id: r.id, date: r.attendance_date, clockIn: r.clock_in_at, shift: r.shift, site: r.site_name, minutes: lateBy, approved: lateApprovals.has(r.id) });
      }
    });

    return Array.from(byPerson.entries()).map(([name, p]) => {
      const base = getSalary(name);
      const perDay = daysInMonth > 0 ? base / daysInMonth : 0;
      const daysPresent = p.days.size;
      const sundaysWorked = monthSundays.filter(d => p.days.has(d)).length;
      const sundaysOff = monthSundays.length - sundaysWorked;
      const paidDays = daysPresent + sundaysOff;
      const gross = perDay * paidDays;
      const sundayBonus = perDay * sundaysWorked; // one extra day's pay per Sunday worked

      const approvedLates = p.lates.filter(l => l.approved).length;
      const lateDeductionDays = Math.floor(approvedLates / LATES_PER_DEDUCTION_DAY);
      const missingOutDeductionDays = Math.floor(p.missingOut / MISSING_OUT_PENALTY_UNIT);
      const deductionDays = lateDeductionDays + missingOutDeductionDays;
      const deductionAmount = perDay * deductionDays;

      const commissions = (commissionByName.get(name) || []).sort((a, b) => a.date.localeCompare(b.date));
      const commission = commissions.reduce((s, c) => s + c.amount, 0);
      const stl = stlByName[name]?.amount || 0;
      const net = Math.max(0, gross + sundayBonus + commission - deductionAmount - stl);
      return {
        name, daysInMonth, daysPresent, missingOut: p.missingOut, checkins: p.checkins,
        sundaysWorked, sundaysOff, paidDays, sundayBonus,
        lates: p.lates.sort((a, b) => a.date.localeCompare(b.date)), approvedLates, lateDeductionDays,
        missingOutDeductionDays, deductionDays, deductionAmount,
        commissions, commission, stl,
        base, perDay, gross, net,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [payrollRecords, payrollMonth, salaries, lateApprovals, stlByName]);

  const downloadPayslip = (row) => {
    const doc = buildPayslipPDF(row, payrollMonth);
    doc.save(`Payslip_${row.name.replace(/\s+/g, '_')}_${payrollMonth}.pdf`);
  };

  const exportPayrollCSV = () => {
    if (!payrollRows.length) return;
    saveCSV(
      `Payroll_${payrollMonth}.csv`,
      ['Name', 'Days Present', 'Sundays Off (paid)', 'Sundays Worked', 'Days In Month', 'Late (total)', 'Late (approved)', 'Late Deduction Days',
        'Missing Clock-outs', 'Missing Clock-out Deduction Days', 'Base Salary', 'Per-Day Rate', 'Gross', 'Sunday Bonus', 'Commission',
        'Deducted Amount', 'STL', 'Net Payable'],
      payrollRows.map(r => [
        csvCell(r.name), r.daysPresent, r.sundaysOff, r.sundaysWorked, r.daysInMonth, r.lates.length, r.approvedLates, r.lateDeductionDays,
        r.missingOut, r.missingOutDeductionDays, r.base, r.perDay.toFixed(2), r.gross.toFixed(2), r.sundayBonus.toFixed(2), r.commission.toFixed(2),
        r.deductionAmount.toFixed(2), r.stl.toFixed(2), r.net.toFixed(2),
      ]),
    );
  };

  const q = search.trim().toLowerCase();

  const enrichedRecords = useMemo(() => {
    const byDay = new Map();
    records.forEach(r => {
      const k = `${r.fo_name}||${r.attendance_date}`;
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k).push(r);
    });

    const meta = new Map();
    byDay.forEach(list => {
      const ordered = [...list].sort(
        (a, b) => new Date(a.clock_in_at || 0) - new Date(b.clock_in_at || 0),
      );
      ordered.forEach((r, i) => meta.set(r.id, { seq: i + 1, of: ordered.length }));
    });

    return records.map(r => {
      const m = meta.get(r.id) || { seq: 1, of: 1 };
      return { ...r, _seq: m.seq, _seqOf: m.of };
    });
  }, [records]);

  // ─── TEAM DATA GENERATION ──────────────────────────────────────────
  const teamsData = useMemo(() => {
    if (!isTeams) return [];
    const grouped = {};
    
    records.forEach(r => {
      const site = r.site_name || r.client_name || UNASSIGNED;
      const date = r.attendance_date;
      if (!date) return;
      
      const key = `${date}::${site}`;
      if (!grouped[key]) {
        grouped[key] = { date, site, supervisors: new Map(), members: new Map() };
      }

      if (checkIsSupervisor(r)) {
        grouped[key].supervisors.set(r.fo_name, r);
      } else {
        grouped[key].members.set(r.fo_name, r);
      }
    });

    // Sirf woh groups rakhein jin mein kam az kam 1 supervisor ho
    let groups = Object.values(grouped).filter(g => g.supervisors.size > 0);
    
    // Applying Search Filter
    if (q) {
      groups = groups.filter(g => 
        g.site.toLowerCase().includes(q) ||
        Array.from(g.supervisors.values()).some(s => (s.fo_name || '').toLowerCase().includes(q)) ||
        Array.from(g.members.values()).some(m => (m.fo_name || '').toLowerCase().includes(q))
      );
    }
    
    // Sort by Date Descending
    return groups.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [records, isTeams, q]);

  const visibleRows = useMemo(() => {
    const filtered = q ? rows.filter(r => (r.label || '').toLowerCase().includes(q)) : rows;
    return sortBy(filtered, sort.key, sort.dir, columns.find(c => c.key === sort.key)?.type || 'text');
  }, [rows, q, sort, columns]);

  const visibleRecords = useMemo(() => {
    let list = enrichedRecords;
    if (attention === 'missing_out') list = list.filter(r => !r.clock_out_at);
    else if (attention === 'unlinked') list = list.filter(r => !r.job_id);
    if (q) list = list.filter(r => (r.fo_name || '').toLowerCase().includes(q));
    return sortBy(list, sort.key, sort.dir, columns.find(c => c.key === sort.key)?.type || 'text');
  }, [enrichedRecords, attention, q, sort, columns]);

  const visibleCoverage = useMemo(() => {
    let list = coverage;
    if (onlyScheduled) list = list.filter(r => Number(r.scheduled_jobs) > 0);
    if (q) {
      list = list.filter(r =>
        (r.client_name || '').toLowerCase().includes(q) ||
        (r.people || []).some(p => (p.fo_name || '').toLowerCase().includes(q)));
    }
    return sortBy(list, sort.key, sort.dir, columns.find(c => c.key === sort.key)?.type || 'text');
  }, [coverage, onlyScheduled, q, sort, columns]);

  const shownCount = isCoverage ? visibleCoverage.length : isTeams ? teamsData.length : isLog ? visibleRecords.length : visibleRows.length;

  const maxima = useMemo(() => {
    const m = {};
    columns.filter(c => c.bar).forEach(c => {
      m[c.key] = visibleRows.reduce((acc, r) => Math.max(acc, Number(r[c.key]) || 0), 0);
    });
    return m;
  }, [columns, visibleRows]);

  const headline = useMemo(() => {
    const explicit = fmtRange(from, to);
    if (explicit) return explicit;
    if (isCoverage && coverageRange) {
      return coverageRange.from === coverageRange.to
        ? fmtDate(coverageRange.from)
        : fmtRange(coverageRange.from, coverageRange.to);
    }
    if (daily.length) return fmtRange(daily[0].date, daily[daily.length - 1].date);
    return 'All time';
  }, [from, to, daily, isCoverage, coverageRange]);

  const totalCheckins = kpis.total_checkins ?? 0;
  const missingOut = kpis.missing_clockout ?? 0;
  const unlinked = kpis.unlinked_checkins ?? 0;

  const workforce = kpis.workforce ?? 0;
  const clockedIn = kpis.clocked_in ?? 0;
  const noClockIn = kpis.no_clock_in ?? 0;

  const hasFilters = !!(clientId || city || attention || q);
  const rangeTag = (from && to)
    ? `${from}_to_${to}`
    : (isCoverage && coverageRange)
      ? (coverageRange.from === coverageRange.to ? coverageRange.from : `${coverageRange.from}_to_${coverageRange.to}`)
      : 'All_Time';

  const exportCSV = () => {
    if (isTeams) {
      if (!teamsData.length) return;
      const flat = teamsData.map(t => {
        const sups = Array.from(t.supervisors.values()).map(s => s.fo_name).join(' | ');
        const mems = Array.from(t.members.values()).map(m => m.fo_name).join(' | ');
        return [csvCell(fmtDate(t.date)), csvCell(t.site), csvCell(sups), csvCell(mems)];
      });
      saveCSV(`Attendance_Teams_${rangeTag}.csv`, ['Date', 'Site', 'Supervisors', 'Members'], flat);
      return;
    }

    const cols = isLog ? LOG_CSV_COLS : columns.filter(c => c.key !== '_photos');

    if (isCoverage) {
      if (!visibleCoverage.length) return;
      const flat = [];
      visibleCoverage.forEach(r => (r.people || []).forEach(p => flat.push([
        csvCell(r.client_name),
        csvCell(r.city),
        r.scheduled_jobs ?? 0,
        csvCell(p.fo_name),
        csvCell(STATES[p.state]?.label || p.state),
        csvCell(p.state === 'elsewhere' ? p.at : ''),
        csvCell(p.last_seen ? fmtDate(p.last_seen) : ''),
        p.shifts ?? 0,
      ])));
      if (!flat.length) return;
      saveCSV(`Attendance_Coverage_${rangeTag}.csv`, ['Client', 'City', 'Jobs Booked', 'Field Officer', 'Status', 'Clocked In At', 'Last Seen For Client', 'Shifts For Client'], flat);
      return;
    }

    if (isLog) {
      if (!visibleRecords.length) return;
      saveCSV(
        `Attendance_Log_${rangeTag}.csv`,
        cols.map(c => c.label),
        visibleRecords.map(r => cols.map(c => {
          if (c.key === 'attendance_date') return csvCell(fmtDate(r.attendance_date));
          if (c.key === 'clock_in_at') return csvCell(fmtTime(r.clock_in_at));
          if (c.key === 'clock_out_at') return csvCell(r.clock_out_at ? fmtTime(r.clock_out_at) : 'no out');
          if (c.hrs) return csvCell(fmtHours(r[c.key]));
          if (c.type === 'num') return r[c.key] != null ? r[c.key] : '—';
          return csvCell(r[c.key]);
        })),
      );
      return;
    }
    
    if (!visibleRows.length) return;
    saveCSV(
      `Attendance_By_${activeView.tab}_${rangeTag}.csv`,
      [...cols.map(c => c.label), 'Last Seen'],
      visibleRows.map(r => [
        ...cols.map(c => (c.hrs ? csvCell(fmtHours(r[c.key])) : c.type === 'num' ? (r[c.key] != null ? r[c.key] : '—') : csvCell(r[c.key]))),
        csvCell(r.last_seen ? fmtDate(r.last_seen) : ''),
      ]),
    );
  };

  const coverageTotals = useMemo(() => visibleCoverage.reduce((acc, r) => ({
    scheduled_jobs: acc.scheduled_jobs + (Number(r.scheduled_jobs) || 0),
    expected: acc.expected + (Number(r.expected) || 0),
    present: acc.present + (Number(r.present) || 0),
    elsewhere: acc.elsewhere + (Number(r.elsewhere) || 0),
    absent: acc.absent + (Number(r.absent) || 0),
  }), { scheduled_jobs: 0, expected: 0, present: 0, elsewhere: 0, absent: 0 }), [visibleCoverage]);

  // Opens every client row that has someone missing (coverage view only).
  const expandAllWithAbsences = () => {
    setExpanded(new Set(coverage.filter(r => Number(r.absent) > 0).map(r => r.client_id)));
  };

  const imgSrc = (u) => (/^(data:|blob:|https?:)/i.test(u) ? u : `${IMG_BASE}${u}`);
  // Server-side resized copy (middleware/uploadThumbs.js): /uploads/...?w=120 returns a ~10-30 KB cached
  // JPEG instead of the multi-MB camera original. Only our own /uploads paths support it.
  const thumbSrc = (u) => (/^\/uploads\//.test(u) ? `${IMG_BASE}${u}?w=120` : imgSrc(u));

  const Thumb = ({ url, tag }) => (
    url ? (
      <img
        src={thumbSrc(url)} alt={tag}
        loading="lazy"
        decoding="async"
        width={40} height={40}
        onClick={(e) => { e.stopPropagation(); setLightboxLoaded(false); setLightbox({ full: imgSrc(url), thumb: thumbSrc(url) }); }}
        style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', cursor: 'zoom-in', border: '1px solid var(--nd-w12)', display: 'block', background: 'var(--nd-w05)' }}
        title={`Clock-${tag.toLowerCase()} photo — click to enlarge`}
      />
    ) : (
      <div title={`No clock-${tag.toLowerCase()} photo`}
        style={{ width: 40, height: 40, borderRadius: 8, border: '1px dashed var(--nd-w10)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nd-w20)', fontSize: 10.5, fontWeight: 600 }}>
        {tag}
      </div>
    )
  );

  const MagnitudeBar = ({ value, max, color }) => {
    const w = max > 0 ? Math.max(2, (Number(value) / max) * 100) : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 56, height: 6, background: 'var(--nd-w06)', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 3 }} />
        </div>
        <span style={{ minWidth: 26 }}>{value ?? 0}</span>
      </div>
    );
  };

  const chip = (key, label, onClear) => (
    <button key={key} className="nv-chip" onClick={onClear} title="Remove this filter">
      {label} <X size={12} style={{ opacity: 0.6 }} />
    </button>
  );

  const exportDisabled = isPayroll ? payrollRows.length === 0 : shownCount === 0;

  const viewHint = isCoverage ? 'Click any row to see the names. Rosters are inferred from who has clocked in for that client before.'
    : (view === 'client' || view === 'city') ? 'Click any row to filter to it and see the people behind the numbers.'
    : isTeams ? 'Team rosters come from Team Management. Site groups are inferred from who worked the same site on the same day.'
    : isPayroll ? `Sundays off are paid; each Sunday worked pays an extra day. Late = ${LATE_GRACE_MIN}+ min after shift start — only approved lates count (${LATES_PER_DEDUCTION_DAY} = 1 day). ${MISSING_OUT_PENALTY_UNIT} missing clock-outs = 1 day. Click a row to approve lates and see commission.`
    : isLog ? 'Every shift is listed — double shifts show "1 of 2 today" under the name. Click a photo to enlarge it.'
    : null;

  const strongText = { color: T.text, fontWeight: 600 };

  return (
    <div className="nv-att" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '24px 28px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      {/* ─── Header ─── */}
      <div className="nv-head">
        <div style={{ minWidth: 0 }}>
          <div className="nv-crumb">C&amp;I Management</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 700, color: T.textStrong, letterSpacing: '-0.02em', lineHeight: 1.2 }}>Attendance</h1>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
            {isPayroll ? (
              <>Payroll for <span style={strongText}>{monthLabel(payrollMonth)}</span></>
            ) : loading ? 'Loading…' : isCoverage ? (
              <>
                <span style={strongText}>{headline}</span>
                {' · '}
                <span style={{ color: noClockIn > 0 ? T.red : T.green, fontWeight: 600 }}>{noClockIn}</span> of{' '}
                <span style={strongText}>{workforce}</span> field staff did not clock in
              </>
            ) : (
              <>
                <span style={strongText}>{headline}</span>
                {' · '}{totalCheckins} check-ins across {kpis.total_jobs ?? 0} jobs · {kpis.people ?? 0} people
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="nv-btn" onClick={fetchData}><RefreshCw size={14} /> Refresh</button>
          <button className="nv-btn nv-primary" onClick={isPayroll ? exportPayrollCSV : exportCSV} disabled={exportDisabled}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* ─── Filters ─── */}
      <div className="nv-filterbar">
        <div className="nv-seg">
          {[['all', 'All time'], ['7', '7 days'], ['30', '30 days'], ['month', 'This month']].map(([key, label]) => (
            <button key={key} className={activePreset === key ? 'nv-on' : undefined} onClick={() => applyPreset(key)}>{label}</button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="date" value={from} aria-label="From date" onChange={e => { setFrom(e.target.value); setActivePreset(''); }} />
          <span style={{ color: T.textMuted, fontSize: 12.5 }}>to</span>
          <input type="date" value={to} aria-label="To date" onChange={e => { setTo(e.target.value); setActivePreset(''); }} />
        </div>

        <select
          value={clientId} aria-label="Client"
          onChange={(e) => {
            const id = e.target.value;
            const found = filterOptions.clients.find(c => String(c.client_id) === id);
            setClientId(id); setClientLabel(found ? found.client_name : '');
          }}
        >
          <option value="">All clients</option>
          {filterOptions.clients.map(c => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
        </select>

        <select value={city} aria-label="City" onChange={(e) => setCity(e.target.value)} style={{ minWidth: 130 }}>
          <option value="">All cities</option>
          {filterOptions.cities.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--nd-tx35)', pointerEvents: 'none' }} />
          <input
            placeholder={isLog ? 'Search name…' : isCoverage ? 'Search client or person…' : isTeams ? 'Search site or person…' : `Search ${activeView.col.toLowerCase()}…`}
            value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 34, width: '100%' }} />
        </div>
      </div>

      {hasFilters && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '-4px 0 16px' }}>
          {attention && chip('attention', attention === 'missing_out' ? 'Missing clock-out' : 'No job linked', () => setAttention(''))}
          {clientId && chip('client', clientLabel || `Client #${clientId}`, () => { setClientId(''); setClientLabel(''); })}
          {city && chip('city', city, () => setCity(''))}
          {q && chip('search', `“${search.trim()}”`, () => setSearch(''))}
          <button className="nv-btn nv-sm nv-ghost" onClick={clearAll}>Clear all</button>
        </div>
      )}

      {error && (
        <div className="nv-card" style={{ padding: '12px 16px', marginBottom: 16, color: T.red, fontSize: 13, borderColor: 'rgba(248,113,113,0.35)' }}>{error}</div>
      )}

      {/* ─── Stats strip ─── */}
      {!isPayroll && (
        <div className="nv-card nv-stats" style={{ marginBottom: 16 }}>
          {(isCoverage ? [
            ['Field staff', workforce, `${kpis.clients ?? 0} clients with a roster`],
            ['Clocked in', clockedIn, `${pct(clockedIn, workforce)}% of field staff`],
          ] : [
            ['Jobs', kpis.total_jobs ?? 0],
            ['Check-ins', totalCheckins],
            ['People', kpis.people ?? 0],
            ['Avg per shift', fmtHours(kpis.avg_hours)],
            ['Active days', kpis.active_days ?? 0],
          ]).map(([label, value, sub]) => (
            <div key={label} className="nv-stat">
              <div className="nv-stat-label">{label}</div>
              <div className="nv-stat-value">{loading ? '—' : value}</div>
              {sub && <div className="nv-stat-sub">{loading ? ' ' : sub}</div>}
            </div>
          ))}

          {(isCoverage ? [
            ['no_clockin', noClockIn, 'No clock-in', T.red, 'rgba(248,113,113,0.08)', `${pct(noClockIn, workforce)}% · show who`, expandAllWithAbsences],
          ] : [
            ['missing_out', missingOut, 'Missing clock-out', T.amber, 'rgba(245,158,11,0.08)', `${pct(missingOut, totalCheckins)}% of check-ins`, () => showAttention('missing_out')],
            ['unlinked', unlinked, 'No job linked', '#A78BFA', 'rgba(167,139,250,0.08)', `${pct(unlinked, totalCheckins)}% of check-ins`, () => showAttention('unlinked')],
          ]).map(([kind, n, label, color, bg, sub, onClick]) => {
            const clear = !loading && !n;
            return (
              <button key={kind} className="nv-stat" onClick={onClick} disabled={loading || clear}
                title={clear ? 'Nothing outstanding in this range' : 'Click to list these'}
                style={{ background: attention === kind ? bg : undefined }}>
                <div className="nv-stat-label">
                  <span className="nv-dot" style={{ background: clear ? T.green : color }} />{label}
                </div>
                <div className="nv-stat-value" style={{ color: clear ? T.textStrong : color }}>{loading ? '—' : n}</div>
                <div className="nv-stat-sub">{loading ? ' ' : clear ? 'All clear' : <>{sub} <span style={{ color: T.blueLight }}>· View</span></>}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* ─── Daily trend (coverage returns no daily series) ─── */}
      {!isPayroll && !isCoverage && (
        <div className="nv-card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px' }}>
            <span className="nv-section-title">Daily activity</span>
            {showTrend && (
              <div style={{ display: 'flex', gap: 14, fontSize: 12.5, color: T.textMuted }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span className="nv-dot" style={{ background: T.blue, borderRadius: 2 }} />Check-ins</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span className="nv-dot" style={{ background: 'rgba(96,165,250,0.4)', borderRadius: 2 }} />Jobs</span>
              </div>
            )}
            <button className="nv-btn nv-sm nv-ghost" style={{ marginLeft: 'auto' }} onClick={() => setShowTrend(s => !s)}>
              {showTrend ? 'Hide' : 'Show'} {showTrend ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
          {showTrend && (
            <div style={{ padding: '0 12px 14px' }}>
              {daily.length === 0 ? (
                <div className="nv-empty" style={{ padding: '24px 0' }}>No data for this range</div>
              ) : (
                <ResponsiveContainer width="100%" height={170}>
                  <BarChart data={daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barGap={3}>
                    <CartesianGrid stroke="var(--nd-w06)" vertical={false} />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: 'var(--nd-tx40)', fontSize: 11.5 }} tickFormatter={(d) => { const p = String(d).split('-'); return p.length === 3 ? `${MONTHS[Number(p[1]) - 1]} ${Number(p[2])}` : d; }} />
                    <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: 'var(--nd-tx40)', fontSize: 11.5 }} />
                    <Tooltip contentStyle={{ background: 'var(--nd-surface-2)', border: '1px solid var(--nd-w10)', borderRadius: 8, color: 'var(--nd-text)', fontSize: 12.5 }} labelFormatter={fmtDate} cursor={{ fill: 'var(--nd-w04)' }} />
                    <Bar dataKey="checkins" name="Check-ins" fill={T.blue} radius={[4, 4, 0, 0]} maxBarSize={22} />
                    <Bar dataKey="jobs" name="Jobs" fill="rgba(96,165,250,0.4)" radius={[4, 4, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Main panel: tabs → content ─── */}
      <div className="nv-card">
        <div className="nv-tabbar">
          <div className="nv-tabs">
            {[...VIEWS, { key: 'payroll', tab: 'Payroll' }].map(v => (
              <button key={v.key} className={`nv-tab${view === v.key ? ' nv-on' : ''}`} onClick={() => switchView(v.key)}>
                {v.tab}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto', padding: '8px 0' }}>
            {isCoverage && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: T.text, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={onlyScheduled} onChange={e => setOnlyScheduled(e.target.checked)}
                  style={{ width: 14, height: 14, accentColor: '#3B82F6', padding: 0 }} />
                Only clients with work booked
              </label>
            )}
            {isPayroll && <input type="month" value={payrollMonth} onChange={e => setPayrollMonth(e.target.value)} style={{ height: 32 }} />}
            {isTeams && (
              <input type="date" value={teamsSelectedDate} onChange={e => setTeamsSelectedDate(e.target.value)}
                title="Attendance status shown for this date" style={{ height: 32 }} />
            )}
            <span className="nv-count">
              {isPayroll ? `${payrollRows.length} people` : isTeams ? `${teamsData.length} site groups` : `${shownCount} ${shownCount === 1 ? 'row' : 'rows'}`}
            </span>
          </div>
        </div>

        {viewHint && <div className="nv-subbar nv-hint">{viewHint}</div>}

        {isPayroll && Object.keys(browserOnlySalaries).length > 0 && (
          <div className="nv-subbar" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'rgba(245,158,11,0.08)', fontSize: 12.5, color: T.text }}>
            <span style={{ flex: '1 1 300px' }}>
              <strong style={{ color: T.amber }}>{Object.keys(browserOnlySalaries).length} salaries</strong> were typed in this browser before salaries were saved
              to the database. Save them now so every admin sees them.
            </span>
            <button className="nv-btn nv-sm nv-primary" disabled={payrollBusy === 'salary-import'} onClick={importBrowserSalaries}>
              {payrollBusy === 'salary-import' ? 'Saving…' : 'Save to database'}
            </button>
          </div>
        )}

        {isTeams && (
          <div style={{ padding: '18px 20px 20px', borderBottom: `1px solid ${T.border}` }}>
            <div className="nv-section-title" style={{ marginBottom: 12 }}>Team rosters · {fmtDate(teamsSelectedDate)}</div>
            {teamRosterLoading ? (
              <div className="nv-empty" style={{ padding: 24 }}>Loading…</div>
            ) : teamRosterList.length === 0 ? (
              <div className="nv-empty" style={{ padding: 24 }}>No teams configured in Team Management.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {teamRosterList.map(team => {
                  const supName = team.team_lead_name || team.team_lead_name_from_users || 'Unknown';
                  const memberIds = team.team_member_user_ids || [];
                  const isOpen = expanded.has(team.id);
                  return (
                    <div key={team.id} style={{ background: 'var(--nd-w04)', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                      <button
                        onClick={() => toggleTeamExpand(team.id)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: "'DM Sans', sans-serif" }}
                      >
                        {isOpen ? <ChevronDown size={15} color="var(--nd-tx35)" /> : <ChevronRight size={15} color="var(--nd-tx35)" />}
                        <span style={{ fontWeight: 700, color: T.text, fontSize: 13.5 }}>{supName}</span>
                        <span className="nv-badge nv-sup">Supervisor</span>
                        <span className="nv-count" style={{ marginLeft: 'auto' }}>
                          {memberIds.length} member{memberIds.length === 1 ? '' : 's'}
                        </span>
                      </button>
                      {isOpen && (
                        <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 16px 14px 41px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                          {memberIds.length === 0 ? (
                            <div className="nv-hint">No members assigned to this team.</div>
                          ) : memberIds.map(uid => {
                            const name = teamUserName(uid);
                            const st = memberDayStatus(name);
                            return (
                              <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 13, color: T.text }}>{name}</span>
                                <span className="nv-badge" style={{ marginLeft: 'auto', color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>{st.label}</span>
                              </div>
                            );
                          })}

                          {addMemberFor === team.id ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                              <select value={newMemberId} onChange={e => setNewMemberId(e.target.value)} style={{ flex: 1, minWidth: 160 }}>
                                <option value="">Select user…</option>
                                {availableUsersForTeam(team).map(u => (
                                  <option key={u.id} value={u.id}>{u.name}</option>
                                ))}
                              </select>
                              <button className="nv-btn nv-sm" onClick={() => handleAddMember(team)} disabled={!newMemberId || addMemberSaving}>
                                {addMemberSaving ? 'Adding…' : 'Add'}
                              </button>
                              <button className="nv-btn nv-sm nv-ghost" onClick={() => { setAddMemberFor(null); setNewMemberId(''); }}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button className="nv-btn nv-sm nv-dashed" style={{ alignSelf: 'flex-start', marginTop: 2 }}
                              onClick={() => { setAddMemberFor(team.id); setNewMemberId(''); }}>
                              <Plus size={13} /> Add member
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {isTeams && (
          <div className="nv-section-title" style={{ padding: '18px 20px 0' }}>Site groups</div>
        )}

        <div className="nv-scroll">
          {isPayroll ? (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th title="Days with a check-in. Sundays not worked are paid days off; each Sunday worked pays one extra day.">Days</th>
                  <th title={`Late = clock-in more than ${LATE_GRACE_MIN} min after shift start; only approved lates count (${LATES_PER_DEDUCTION_DAY} = 1 day). Every ${MISSING_OUT_PENALTY_UNIT} missing clock-outs = 1 day.`}>Deductions</th>
                  <th>Base salary</th>
                  <th title="Short Term Loan instalment deducted this month">STL</th>
                  <th title={`(site kW − ${COMMISSION_BASE_KW}) ÷ people on site that day × Rs. ${COMMISSION_RS_PER_KW}`}>Commission</th>
                  <th>Net payable</th>
                  <th>Payslip</th>
                </tr>
              </thead>
              <tbody>
                {payrollLoading ? (
                  <tr><td colSpan={8} className="nv-empty">Loading…</td></tr>
                ) : payrollError ? (
                  <tr><td colSpan={8} className="nv-empty" style={{ color: T.red }}>{payrollError}</td></tr>
                ) : payrollRows.length === 0 ? (
                  <tr><td colSpan={8} className="nv-empty">No attendance data for {monthLabel(payrollMonth)}</td></tr>
                ) : payrollRows.map(row => {
                  const open = payrollOpen === row.name;
                  const pendingLates = row.lates.length - row.approvedLates;
                  return (
                    <React.Fragment key={row.name}>
                      <tr className="nv-drill" onClick={() => setPayrollOpen(open ? null : row.name)} title={open ? 'Hide details' : 'Show lates and commission'}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 6, color: T.textMuted }}>
                            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </span>
                          <span style={{ fontWeight: 600, color: T.textStrong }}>{row.name}</span>
                        </td>
                        <td className="nv-num">
                          {row.daysPresent} present
                          <div className="nv-sub">{row.sundaysOff > 0 ? `+${row.sundaysOff} Sun off (paid)` : `${row.paidDays} / ${row.daysInMonth} paid`}</div>
                          {row.sundaysWorked > 0 && (
                            <div className="nv-sub" style={{ color: T.blueLight }}>{row.sundaysWorked} Sun worked · +{fmtRs(row.sundayBonus)}</div>
                          )}
                        </td>
                        <td className="nv-num">
                          {row.deductionAmount > 0
                            ? <span style={{ color: T.red, fontWeight: 600 }}>−{fmtRs(row.deductionAmount)}</span>
                            : <span style={{ color: 'var(--nd-w20)' }}>—</span>}
                          {row.lates.length > 0 && (
                            <div className="nv-sub" style={{ color: pendingLates > 0 ? T.amber : undefined }}>
                              Late {row.lates.length} · {row.approvedLates} approved{pendingLates > 0 ? ` · ${pendingLates} pending` : ''}
                            </div>
                          )}
                          {row.missingOut > 0 && <div className="nv-sub">No clock-out {row.missingOut}</div>}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <input
                            key={`${row.name}-${row.base}`}
                            type="number" min={0} step={500} defaultValue={row.base}
                            disabled={payrollBusy === `salary-${row.name}`}
                            onBlur={e => saveSalary(row.name, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            style={{ width: 96, borderColor: salaries[row.name] == null ? 'rgba(245,158,11,0.45)' : undefined }}
                            title={salaries[row.name] == null
                              ? `Not set — using the default Rs. ${DEFAULT_SALARY.toLocaleString()}. Type the salary and press Enter to save it.`
                              : 'Base salary — saved for everyone. Edit and press Enter to change it.'}
                          />
                          <div className="nv-sub" style={salaries[row.name] == null ? { color: T.amber } : undefined}>{salaries[row.name] == null ? 'Default · not saved' : `${fmtRs(row.perDay)} / day`}</div>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <input
                            key={`${row.name}-${row.stl}`}
                            type="number" min={0} step={500} defaultValue={row.stl || ''} placeholder="0"
                            disabled={payrollBusy === `stl-${row.name}`}
                            onBlur={e => saveStl(row.name, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            style={{ width: 88 }}
                            title={`Short Term Loan to deduct for ${monthLabel(payrollMonth)} — saved for everyone`}
                          />
                        </td>
                        <td className="nv-num">
                          {row.commission > 0 ? (
                            <>
                              <span style={{ color: T.green }}>+{fmtRs(row.commission)}</span>
                              <div className="nv-sub">{row.commissions.length} site-day{row.commissions.length === 1 ? '' : 's'}</div>
                            </>
                          ) : <span style={{ color: 'var(--nd-w20)' }}>—</span>}
                        </td>
                        <td className="nv-num" style={{ fontWeight: 700, color: T.green, fontSize: 14 }}>{fmtRs(row.net)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <button className="nv-btn nv-sm" onClick={() => downloadPayslip(row)}><Download size={13} /> PDF</button>
                        </td>
                      </tr>

                      {open && (
                        <tr>
                          <td colSpan={8} style={{ background: 'var(--nd-w03, rgba(255,255,255,0.02))', padding: '16px 20px 20px 42px' }}>
                            <div className="nv-sub" style={{ marginTop: 0, marginBottom: 14, fontSize: 12.5 }}>
                              {fmtRs(row.perDay)} × {row.paidDays} paid days ({row.daysPresent} present + {row.sundaysOff} Sundays off) = {fmtRs(row.gross)}
                              {row.sundayBonus > 0 && <> · + Sunday work {fmtRs(row.sundayBonus)}</>}
                              {row.commission > 0 && <> · + commission {fmtRs(row.commission)}</>}
                              {row.deductionAmount > 0 && <> · − deductions {fmtRs(row.deductionAmount)}</>}
                              {row.stl > 0 && <> · − STL {fmtRs(row.stl)}</>}
                              {' '}= <strong style={{ color: T.green }}>{fmtRs(row.net)}</strong>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20 }}>
                              <div>
                                <div className="nv-section-title" style={{ fontSize: 13, marginBottom: 8 }}>
                                  Late check-ins <span style={{ color: T.textMuted, fontWeight: 500 }}>· {row.approvedLates} of {row.lates.length} approved · every {LATES_PER_DEDUCTION_DAY} approved = 1 day</span>
                                </div>
                                {row.lates.length === 0 ? (
                                  <div className="nv-hint">No late check-ins this month.</div>
                                ) : row.lates.map(l => (
                                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--nd-w05)', fontSize: 12.5 }}>
                                    <span style={{ width: 92, color: T.textMuted }}>{fmtDate(l.date)}</span>
                                    <span style={{ width: 70 }}>{fmtTime(l.clockIn)}</span>
                                    <span style={{ width: 92, color: T.amber }}>{fmtLate(l.minutes)}</span>
                                    <span style={{ flex: 1, minWidth: 0, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                                      {l.shift} shift{l.site ? ` · ${l.site}` : ''}
                                    </span>
                                    <button
                                      className={`nv-btn nv-sm${l.approved ? '' : ' nv-primary'}`}
                                      disabled={payrollBusy === `late-${l.id}`}
                                      onClick={() => toggleLateApproval(l.id, !l.approved)}
                                      title={l.approved ? 'Approved — counts toward the deduction. Click to undo.' : 'Approve this late so it counts toward the deduction'}
                                      style={l.approved ? { color: T.green } : undefined}>
                                      {l.approved ? '✓ Approved' : 'Approve'}
                                    </button>
                                  </div>
                                ))}
                              </div>

                              <div>
                                <div className="nv-section-title" style={{ fontSize: 13, marginBottom: 8 }}>
                                  Commission <span style={{ color: T.textMuted, fontWeight: 500 }}>· clients with Commission ON · sites over {COMMISSION_BASE_KW} kW</span>
                                </div>
                                {row.commissions.length === 0 ? (
                                  <div className="nv-hint">No commission this month — none of this person's sites belong to a client with Commission ON (Mandate Config) and over {COMMISSION_BASE_KW} kW.</div>
                                ) : row.commissions.map(c => (
                                  <div key={`${c.date}-${c.site}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--nd-w05)', fontSize: 12.5 }}>
                                    <span style={{ width: 92, color: T.textMuted }}>{fmtDate(c.date)}</span>
                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.client || ''}>{c.site}</span>
                                    <span style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>({c.kw} − {COMMISSION_BASE_KW}) kW ÷ {c.people} × {COMMISSION_RS_PER_KW}</span>
                                    <span style={{ width: 80, textAlign: 'right', color: T.green, fontWeight: 600 }}>{fmtRs(c.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              {!payrollLoading && payrollRows.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={6} style={{ color: T.textMuted, fontWeight: 600 }}>Total net payable</td>
                    <td className="nv-num" style={{ color: T.green }}>{fmtRs(payrollRows.reduce((s, r) => s + r.net, 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          ) : isTeams ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14, padding: '12px 20px 20px' }}>
              {loading ? (
                <div className="nv-empty" style={{ gridColumn: '1 / -1' }}>Loading…</div>
              ) : teamsData.length === 0 ? (
                <div className="nv-empty" style={{ gridColumn: '1 / -1' }}>No site groups found — needs at least one supervisor and members at a site on the same day.</div>
              ) : teamsData.map((team, idx) => (
                <div key={idx} style={{ background: 'var(--nd-w04)', border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
                  <div className="nv-count" style={{ marginBottom: 5 }}>{fmtDate(team.date)}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.textStrong, marginBottom: 14 }}>{team.site}</div>

                  <div style={{ marginBottom: 14 }}>
                    <div className="nv-fieldlabel" style={{ color: T.blueLight }}>Supervisor(s)</div>
                    {Array.from(team.supervisors.values()).map(s => (
                      <div key={s.id || s.fo_name} style={{ fontSize: 13, fontWeight: 700, color: T.text, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.blueLight }} /> {s.fo_name}
                      </div>
                    ))}
                  </div>

                  <div>
                    <div className="nv-fieldlabel">Team members</div>
                    {team.members.size === 0 ? (
                      <div className="nv-hint">No other members present</div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {Array.from(team.members.values()).map(m => (
                          <span key={m.id || m.fo_name} style={{ fontSize: 12, background: 'var(--nd-surface)', border: `1px solid ${T.border}`, padding: '5px 10px', borderRadius: 6, color: T.text }}>
                            {m.fo_name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  {columns.map(c => {
                    const on = sort.key === c.key;
                    return (
                      <th key={c.key}
                        className={`${c.sortable === false ? '' : 'nv-sortable'}${on ? ' nv-active' : ''}`}
                        onClick={() => toggleSort(c)}
                        title={c.hint || (c.sortable === false ? undefined : 'Sort')}>
                        {c.label}
                        {on && <span style={{ marginLeft: 5 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr><td colSpan={columns.length} className="nv-empty">Loading…</td></tr>
                ) : isCoverage ? (
                  visibleCoverage.length === 0 ? (
                    <tr><td colSpan={columns.length} className="nv-empty">No clients match these filters</td></tr>
                  ) : visibleCoverage.map(r => {
                    const open = expanded.has(r.client_id);
                    // A client with nothing booked still lists its roster, but
                    // those absences are not no-shows — greyed, never red.
                    const idle = Number(r.scheduled_jobs) === 0;
                    return (
                      <React.Fragment key={r.client_id}>
                        <tr className="nv-drill" onClick={() => toggleTeamExpand(r.client_id)}
                          title={open ? 'Hide names' : 'Show who was there and who was not'}>
                          <td style={{ fontWeight: 700 }}>
                            <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 6, color: T.textMuted }}>
                              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </span>
                            {r.client_name}
                          </td>
                          <td style={{ color: T.textMuted }}>{r.city || '—'}</td>
                          <td className="nv-num" style={{ color: idle ? 'var(--nd-w20)' : T.text }}>{r.scheduled_jobs ?? 0}</td>
                          <td className="nv-num">{r.expected ?? 0}</td>
                          <td className="nv-num" style={{ color: r.present > 0 ? T.green : T.textMuted }}>{r.present ?? 0}</td>
                          <td className="nv-num" style={{ color: r.elsewhere > 0 ? T.amber : T.textMuted }}>{r.elsewhere ?? 0}</td>
                          <td className="nv-num" style={{ color: (r.absent > 0 && !idle) ? T.red : T.textMuted, fontWeight: (r.absent > 0 && !idle) ? 700 : 400 }}>
                            {r.absent ?? 0}
                            {idle && r.absent > 0 && <span style={{ fontSize: 10.5, color: 'var(--nd-w20)', marginLeft: 6, fontWeight: 400 }}>nothing booked</span>}
                          </td>
                          <td className="nv-num" style={{ color: T.textMuted }}>{r.coverage_pct}%</td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={columns.length} style={{ background: 'var(--nd-w04)', padding: '14px 20px 16px 42px' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {(r.people || []).map(p => {
                                  const s = STATES[p.state] || STATES.absent;
                                  const seen = p.last_seen ? fmtDate(p.last_seen) : 'never';
                                  return (
                                    <span key={`${r.client_id}-${p.fo_name}`}
                                      title={`${s.label}${p.state === 'elsewhere' && p.at ? ` — at ${p.at}` : ''} · last worked this client ${seen} · ${p.shifts} shift${Number(p.shifts) === 1 ? '' : 's'} here`}
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, color: s.color, background: s.bg, border: `1px solid ${s.border}` }}>
                                      {p.fo_name}
                                      <span style={{ fontSize: 10.5, opacity: 0.8, fontWeight: 400 }}>
                                        {p.state === 'elsewhere' && p.at ? p.at : seen}
                                      </span>
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : isLog ? (
                  visibleRecords.length === 0 ? (
                    <tr><td colSpan={columns.length} className="nv-empty">No check-ins match these filters</td></tr>
                  ) : visibleRecords.map(r => (
                    <tr key={r.id}>
                      <td style={{ minWidth: 180 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: T.textStrong }}>
                          {r.fo_name}
                          {checkIsSupervisor(r) && <span className="nv-badge nv-sup">Supervisor</span>}
                        </div>
                        <div className="nv-sub">
                          {r.shift ? `${r.shift.charAt(0).toUpperCase()}${r.shift.slice(1)} shift` : 'Shift —'}
                          {r._seqOf > 1 && (
                            <span title={`Shift ${r._seq} of ${r._seqOf} worked that day — all of them count`} style={{ color: T.blueLight }}>
                              {' · '}{r._seq} of {r._seqOf} today
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Thumb url={r.clock_in_image_url} tag="In" />
                          <Thumb url={r.clock_out_image_url} tag="Out" />
                        </div>
                      </td>
                      <td className="nv-num" style={{ color: T.textMuted }}>{fmtDate(r.attendance_date)}</td>
                      <td className="nv-num">{fmtTime(r.clock_in_at)}</td>
                      <td className="nv-num">
                        {r.clock_out_at ? fmtTime(r.clock_out_at)
                          : <span className="nv-badge" style={{ color: T.amber, background: 'rgba(245,158,11,0.12)' }}>No clock-out</span>}
                      </td>
                      <td className="nv-num" style={{ fontWeight: 600 }}>{r.hours_worked != null ? fmtHours(r.hours_worked) : <span style={{ color: 'var(--nd-w20)' }}>—</span>}</td>
                      <td style={{ minWidth: 200, maxWidth: 320 }}>
                        <div style={{ color: r.client_name ? T.text : T.textMuted }}>{r.client_name || UNASSIGNED}</div>
                        <div className="nv-sub">{[r.site_name, r.city].filter(Boolean).join(' · ') || '—'}</div>
                      </td>
                      <td>
                        <span className="nv-badge" style={{ textTransform: 'capitalize', color: T.green, background: 'rgba(34,211,165,0.12)' }}>{r.status || '—'}</span>
                      </td>
                    </tr>
                  ))
                ) : visibleRows.length === 0 ? (
                  <tr><td colSpan={columns.length} className="nv-empty">No data for these filters</td></tr>
                ) : visibleRows.map(r => (
                  <tr key={`${r.group_id ?? 'x'}-${r.label}`}
                    className={canDrill(r) ? 'nv-drill' : undefined}
                    onClick={() => canDrill(r) && drillInto(r)}
                    title={canDrill(r) ? `Filter to ${r.label}` : undefined}>
                    <td style={{ fontWeight: 700, color: r.label === UNASSIGNED ? T.textMuted : T.text }}>
                      {r.label}
                      {view === 'person' && checkIsSupervisor(r) && <span className="nv-badge nv-sup" style={{ marginLeft: 8 }}>Supervisor</span>}
                      {r.label === UNASSIGNED && <span style={{ fontSize: 11, color: 'var(--nd-w20)', marginLeft: 8, fontWeight: 400 }}>no job link</span>}
                    </td>
                    {columns.slice(1).map(c => (
                      <td key={c.key} className="nv-num"
                        style={c.warn ? { color: r[c.key] > 0 ? T.amber : T.textMuted } : undefined}>
                        {c.bar
                          ? <MagnitudeBar value={r[c.key] ?? 0} max={maxima[c.key]} color={c.bar} />
                          : c.hrs ? fmtHours(r[c.key])
                          : (r[c.key] != null ? r[c.key] : '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>

              {!loading && shownCount > 0 && (
                <tfoot>
                  <tr>
                    <td style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>
                      Total
                      {q && !isCoverage && <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--nd-w20)', marginLeft: 8 }}>all rows — search not applied</span>}
                      {isCoverage && <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--nd-w20)', marginLeft: 8 }}>rows shown</span>}
                    </td>
                    {isCoverage ? (
                      <>
                        <td />
                        <td className="nv-num">{coverageTotals.scheduled_jobs}</td>
                        <td className="nv-num">{coverageTotals.expected}</td>
                        <td className="nv-num" style={{ color: T.green }}>{coverageTotals.present}</td>
                        <td className="nv-num" style={{ color: T.amber }}>{coverageTotals.elsewhere}</td>
                        <td className="nv-num" style={{ color: T.red }}>{coverageTotals.absent}</td>
                        <td className="nv-num">{pct(coverageTotals.present, coverageTotals.expected)}%</td>
                      </>
                    ) : isLog ? (
                      <>
                        <td colSpan={4} />
                        <td className="nv-num">
                          {fmtHours(visibleRecords.reduce((s, r) => s + (Number(r.hours_worked) || 0), 0))}
                        </td>
                        <td colSpan={2} />
                      </>
                    ) : (
                      columns.slice(1).map(c => (
                        <td key={c.key} className="nv-num" style={c.warn ? { color: T.amber } : undefined}>
                          {c.key === 'jobs' ? (kpis.total_jobs ?? 0)
                            : c.key === 'checkins' ? totalCheckins
                            : c.key === 'people' ? (kpis.people ?? 0)
                            : c.key === 'days' ? (kpis.active_days ?? 0)
                            : c.key === 'total_hours' ? fmtHours(kpis.total_hours)
                            : c.key === 'avg_hours' ? fmtHours(kpis.avg_hours)
                            : c.key === 'missing_clockout' ? missingOut
                            : '—'}
                        </td>
                      ))
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>

        {isCoverage && (
          <div className="nv-foot">
            <strong style={{ color: T.text }}>Expected</strong> is inferred from history — everyone who has clocked in for that client
            before. The system holds no FO-to-client assignment list, so a newly assigned officer only appears here after their
            first clock-in at that client. <strong style={{ color: T.amber }}>Elsewhere</strong> means they were at work, just for
            another client — only <strong style={{ color: T.red }}>No clock-in</strong> is a genuine absence.
            {' '}Rows where <strong>Jobs booked</strong> is 0 had no work scheduled in this range. People on several rosters count more than once in the totals.
          </div>
        )}

        {!isLog && !isPayroll && !isTeams && !isCoverage && (
          <div className="nv-foot">
            Totals are counted over the whole filtered range, not summed from the rows above — a job worked by two people counts once.
            {missingOut > 0 && (
              <> Hours cover only shifts with a clock-out; <strong style={{ color: T.amber }}>{missingOut}</strong> check-in{missingOut === 1 ? ' has' : 's have'} none, so hours read low.</>
            )}
          </div>
        )}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 24 }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Cached thumbnail stretched + blurred as a placeholder while the original downloads */}
            {!lightboxLoaded && (
              <img src={lightbox.thumb} alt="" aria-hidden="true"
                style={{ width: 'min(70vw, 560px)', maxHeight: '88vh', objectFit: 'contain', borderRadius: 12, filter: 'blur(6px)', opacity: 0.7 }} />
            )}
            <img src={lightbox.full} alt="Attendance" onLoad={() => setLightboxLoaded(true)}
              style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 12, border: '1px solid var(--nd-w15)', display: lightboxLoaded ? 'block' : 'none' }} />
            {!lightboxLoaded && (
              <div style={{ position: 'absolute', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 999, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 13, fontWeight: 600 }}>
                <Loader2 size={15} className="nv-spin" /> Loading full photo…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceDashboard;